import * as d3 from 'd3';
import {geoAlbers, geoAlbersUsa, geoPath} from 'd3-geo';
import type * as geojson from 'geojson';
import React, {useEffect, useRef} from 'react';
import {feature} from 'topojson-client';
import type {GeometryCollection, Topology} from 'topojson-specification';

const STATES_URL = '/regional-report-states.json';
const MAP_HEIGHT = 320;

/** PNW bounding box: roughly [lon_min, lat_min, lon_max, lat_max] */
const PNW_BBOX = [-125, 42, -115, 49];

type SiteMarker = {
  name: string;
  lat: number | null;
  lon: number | null;
  medianPctNormal?: number | null;
};

type Props = {
  sites: SiteMarker[];
  basinOutline?: geojson.Geometry | null;
};

function markerColor(pct: number | null | undefined): string {
  if (pct == null) return '#6b7280';
  if (pct < 75) return '#dc2626';
  if (pct < 90) return '#f97316';
  if (pct <= 110) return '#16a34a';
  if (pct <= 125) return '#0ea5e9';
  return '#2563eb';
}

type ProjectedMarker = {name: string; x: number; y: number; medianPctNormal?: number | null};

const LABEL_FONT_SIZE = 9;
const LABEL_CHAR_WIDTH = 5; // approx px per character at font-size 9
const LABEL_HEIGHT = 12;
const LABEL_PADDING = 2;
const MARKER_CLEARANCE = 8;

type LabelBox = {x: number; y: number; w: number; h: number};

function boxOverlaps(a: LabelBox, b: LabelBox): boolean {
  return !(a.x + a.w < b.x || b.x + b.w < a.x || a.y + a.h < b.y || b.y + b.h < a.y);
}

/**
 * Greedy collision resolver: place each label at its preferred (east-of-marker)
 * position, then, if it overlaps an already-placed label, try a small set of
 * fallback anchors (NE, N, NW, W, SW, S, SE) until one fits. Handles the dense
 * south-central Columbia cluster (Hungry Horse / Albeni Falls / Priest Rapids /
 * Long Lake / Dworshak / Ice Harbor / Whitebird / The Dalles) that the previous
 * vertical-only stagger couldn't separate.
 */
function placeLabels(
  markers: ProjectedMarker[]
): Array<{x: number; y: number; anchor: 'start' | 'end'}> {
  const placed: LabelBox[] = [];
  const positions: Array<{x: number; y: number; anchor: 'start' | 'end'}> = [];

  // Candidate offsets relative to the marker center. First entry is the
  // preferred position; subsequent entries are fallbacks in order of desirability.
  const candidates: Array<{dx: number; dy: number; anchor: 'start' | 'end'}> = [
    {dx: MARKER_CLEARANCE, dy: 3, anchor: 'start'}, // E (default)
    {dx: -MARKER_CLEARANCE, dy: 3, anchor: 'end'}, // W
    {dx: MARKER_CLEARANCE, dy: -8, anchor: 'start'}, // NE
    {dx: -MARKER_CLEARANCE, dy: -8, anchor: 'end'}, // NW
    {dx: MARKER_CLEARANCE, dy: 14, anchor: 'start'}, // SE
    {dx: -MARKER_CLEARANCE, dy: 14, anchor: 'end'}, // SW
    {dx: MARKER_CLEARANCE, dy: -14, anchor: 'start'}, // NNE
    {dx: -MARKER_CLEARANCE, dy: -14, anchor: 'end'}, // NNW
    {dx: MARKER_CLEARANCE, dy: 20, anchor: 'start'}, // SSE
    {dx: -MARKER_CLEARANCE, dy: 20, anchor: 'end'}, // SSW
  ];

  for (const m of markers) {
    const w = m.name.length * LABEL_CHAR_WIDTH + 2 * LABEL_PADDING;
    let chosen = candidates[0];
    let chosenBox: LabelBox | null = null;
    for (const c of candidates) {
      // Label box is positioned based on anchor: 'start' means x is the left
      // edge of the text; 'end' means x is the right edge.
      const boxX = c.anchor === 'start' ? m.x + c.dx : m.x + c.dx - w;
      const box: LabelBox = {x: boxX, y: m.y + c.dy - LABEL_HEIGHT + 2, w, h: LABEL_HEIGHT};
      if (!placed.some((p) => boxOverlaps(box, p))) {
        chosen = c;
        chosenBox = box;
        break;
      }
    }
    if (chosenBox == null) {
      // All candidates collide — use the default and accept the overlap rather
      // than drop the label entirely.
      const boxX = chosen.anchor === 'start' ? m.x + chosen.dx : m.x + chosen.dx - w;
      chosenBox = {x: boxX, y: m.y + chosen.dy - LABEL_HEIGHT + 2, w, h: LABEL_HEIGHT};
    }
    placed.push(chosenBox);
    positions.push({x: m.x + chosen.dx, y: m.y + chosen.dy, anchor: chosen.anchor});
  }
  return positions;
}

function drawMarkers(
  svg: d3.Selection<SVGSVGElement, unknown, null, undefined>,
  markers: ProjectedMarker[]
) {
  const positions = placeLabels(markers);

  // Screen variant: small markers with text labels + leader lines. Hidden in
  // print because the labels overlap badly at letter-page scale.
  const screenG = svg.append('g').attr('class', 'rr-map-screen');
  for (let i = 0; i < markers.length; i += 1) {
    const p = markers[i];
    const pos = positions[i];
    screenG
      .append('circle')
      .attr('cx', p.x)
      .attr('cy', p.y)
      .attr('r', 5)
      .attr('fill', markerColor(p.medianPctNormal))
      .attr('stroke', '#fff')
      .attr('stroke-width', 1.5);
    const offsetLen = Math.hypot(pos.x - p.x, pos.y - p.y);
    if (offsetLen > MARKER_CLEARANCE + 4) {
      screenG
        .append('line')
        .attr('x1', p.x)
        .attr('y1', p.y)
        .attr('x2', pos.x + (pos.anchor === 'start' ? -2 : 2))
        .attr('y2', pos.y - 2)
        .attr('stroke', '#9ca3af')
        .attr('stroke-width', 0.5);
    }
    screenG
      .append('text')
      .attr('x', pos.x)
      .attr('y', pos.y)
      .attr('text-anchor', pos.anchor)
      .attr('font-size', LABEL_FONT_SIZE)
      .attr('fill', '#374151')
      .attr('paint-order', 'stroke')
      .attr('stroke', '#ffffff')
      .attr('stroke-width', 2.5)
      .text(p.name);
  }

  // Print variant: numbered markers, no text labels. Numbers tie back to a
  // legend rendered below the SVG.
  const printG = svg.append('g').attr('class', 'rr-map-print');
  for (let i = 0; i < markers.length; i += 1) {
    const p = markers[i];
    printG
      .append('circle')
      .attr('cx', p.x)
      .attr('cy', p.y)
      .attr('r', 8)
      .attr('fill', markerColor(p.medianPctNormal))
      .attr('stroke', '#fff')
      .attr('stroke-width', 1.5);
    printG
      .append('text')
      .attr('x', p.x)
      .attr('y', p.y)
      .attr('text-anchor', 'middle')
      .attr('dominant-baseline', 'central')
      .attr('font-size', 9)
      .attr('font-weight', 700)
      .attr('fill', '#fff')
      .text(i + 1);
  }
}

export default function RegionalMap({sites, basinOutline}: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);

  const validSites = sites.filter((s) => s.lat != null && s.lon != null);

  useEffect(() => {
    if (!svgRef.current || !containerRef.current || validSites.length === 0) return;

    const containerWidth = containerRef.current.clientWidth || 600;

    const render = async () => {
      if (!svgRef.current) return;

      const svg = d3.select(svgRef.current);
      svg.selectAll('*').remove();
      svg
        .attr('viewBox', `0 0 ${containerWidth} ${MAP_HEIGHT}`)
        .attr('preserveAspectRatio', 'xMidYMid meet');

      // Backend-supplied basin outline path: project with an Albers conic
      // tuned for PNW+BC (AlbersUsa chokes on Canadian coordinates).
      if (basinOutline) {
        const feat: geojson.Feature = {type: 'Feature', geometry: basinOutline, properties: {}};
        const projection = geoAlbers()
          .rotate([120, 0])
          .parallels([43, 48])
          .fitSize([containerWidth - 20, MAP_HEIGHT - 20], feat as any);
        const path = geoPath().projection(projection);

        // Muted state outlines behind the basin (best-effort; skip silently).
        try {
          const res = await fetch(STATES_URL);
          const us = (await res.json()) as Topology<{states: GeometryCollection}>;
          const states = feature(us, us.objects.states);
          svg
            .append('g')
            .selectAll('path')
            .data((states as any).features)
            .join('path')
            .attr('d', path as any)
            .attr('fill', 'none')
            .attr('stroke', '#d1d5db')
            .attr('stroke-width', 0.4)
            .attr('opacity', 0.6);
        } catch {
          /* ignore */
        }

        svg
          .append('path')
          .attr('d', path(feat as any) ?? '')
          .attr('fill', '#e0f2fe')
          .attr('stroke', '#0369a1')
          .attr('stroke-width', 0.8)
          .attr('fill-opacity', 0.7);

        const projected: ProjectedMarker[] = validSites.flatMap((s) => {
          const coords = projection([s.lon!, s.lat!]);
          return coords
            ? [{name: s.name, x: coords[0], y: coords[1], medianPctNormal: s.medianPctNormal}]
            : [];
        });

        drawMarkers(svg, projected);
        return;
      }

      // Fallback: AlbersUsa + states basemap.
      const res = await fetch(STATES_URL);
      const us = (await res.json()) as Topology<{states: GeometryCollection}>;
      const states = feature(us, us.objects.states);

      const projection = geoAlbersUsa().fitSize([containerWidth - 40, MAP_HEIGHT - 40], {
        type: 'FeatureCollection',
        features: (states as any).features.filter((f: any) => {
          const pnwStates = ['41', '53', '16', '30', '06', '32', '56'];
          return pnwStates.includes(f.id);
        }),
      } as any);

      const projectedCenter = projection([
        (PNW_BBOX[0] + PNW_BBOX[2]) / 2,
        (PNW_BBOX[1] + PNW_BBOX[3]) / 2,
      ]);
      if (projectedCenter) {
        const [cx, cy] = projectedCenter;
        const [tx, ty] = projection.translate();
        projection.translate([tx + containerWidth / 2 - cx, ty + MAP_HEIGHT / 2 - cy]);
      }

      const path = geoPath().projection(projection);

      svg
        .append('g')
        .selectAll('path')
        .data((states as any).features)
        .join('path')
        .attr('d', path as any)
        .attr('fill', '#f3f4f6')
        .attr('stroke', '#d1d5db')
        .attr('stroke-width', 0.5);

      const projected: ProjectedMarker[] = validSites.flatMap((s) => {
        const coords = projection([s.lon!, s.lat!]);
        return coords
          ? [{name: s.name, x: coords[0], y: coords[1], medianPctNormal: s.medianPctNormal}]
          : [];
      });

      drawMarkers(svg, projected);
    };

    render().catch((err) => console.error('Failed to render regional map:', err));
  }, [validSites, basinOutline]);

  if (validSites.length === 0) return null;

  return (
    <div ref={containerRef} className="w-full">
      <svg ref={svgRef} className="w-full" style={{height: MAP_HEIGHT}} />
      <div className="flex items-center gap-4 mt-1 text-[10px] text-gray-500 flex-wrap justify-center">
        <span className="flex items-center gap-1">
          <span className="inline-block w-2 h-2 rounded-full bg-red-600" /> {'<'}75%
        </span>
        <span className="flex items-center gap-1">
          <span className="inline-block w-2 h-2 rounded-full bg-orange-500" /> 75-90%
        </span>
        <span className="flex items-center gap-1">
          <span className="inline-block w-2 h-2 rounded-full bg-green-600" /> 90-110%
        </span>
        <span className="flex items-center gap-1">
          <span className="inline-block w-2 h-2 rounded-full bg-sky-500" /> 110-125%
        </span>
        <span className="flex items-center gap-1">
          <span className="inline-block w-2 h-2 rounded-full bg-blue-600" /> {'>'}125%
        </span>
        <span className="text-gray-400 ml-1">% of normal</span>
      </div>
      {/* Numbered legend, only shown in print where the on-map text labels
          would otherwise overlap. */}
      <div className="hidden print:grid grid-cols-2 gap-x-6 gap-y-1 mt-3 text-[9pt] text-gray-700">
        {validSites.map((s, i) => (
          <div key={s.name} className="flex items-center gap-2">
            <span
              className="inline-flex items-center justify-center w-4 h-4 rounded-full text-white text-[7pt] font-bold shrink-0"
              style={{background: markerColor(s.medianPctNormal)}}
            >
              {i + 1}
            </span>
            <span>{s.name}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
